import { ExpenseAccount } from "@finway-group/shared/lib/models"
import { isExpenseAccountCodeDATEVCompliant } from "@finway-group/shared/lib/utils/datevValidation"
import { Form, Modal } from "antd"
import { Store } from "antd/lib/form/interface"
import ExpenseAccountForm from "Components/forms/expenseAccount.form"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useCompany } from "Shared/hooks/company.hooks"
import { NotificationService } from "Shared/services"
import DialogService from "Shared/services/dialog.service"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { updateExpenseAccounts, createExpenseAccounts } from "Shared/store/actions/expenseAccount/expenseAccountActions"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

interface ExpenseAccountModalInterface {
    isShowing: boolean
    expenseAccounts: ExpenseAccount[]
    isEdit?: boolean
    onCancel: () => void
}
const ExpenseAccountModal: React.FC<ExpenseAccountModalInterface> = ({ expenseAccounts, isShowing, onCancel, isEdit = true }) => {
    const { t } = useTranslation()
    const [formInstance] = Form.useForm()
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const allowSpecialCharCodes = useCompany()?.allowSpecialCharactersInExpenseAccountCodesForDatev

    const dispatch = useDispatch()

    useEffect(() => {
        if (isShowing) formInstance.setFieldsValue({ expenseAccounts, isEdit })
    }, [isShowing])

    const onSubmit = async (values: Store) => {
        try {
            const datevIncompatible = values.expenseAccounts.some((value: ExpenseAccount) => !isExpenseAccountCodeDATEVCompliant(value.accountCode))

            if (datevIncompatible) {
                if (!allowSpecialCharCodes) {
                    NotificationService.send(NotificationTypeEnum.ERROR, t("error:expense_account.incompatible.title"), t("error:expense_account.incompatible.message"))
                    return
                }
                const isConfirmed = await DialogService.confirmExpenseAccountCodeDatevCompliance()
                if (!isConfirmed) return
            }

            const count = values.expenseAccounts.length
            const msgKey = isEdit ? "updated" : "saved"

            setIsLoading(true)

            if (isEdit) {
                await dispatch(updateExpenseAccounts(values.expenseAccounts))
            } else {
                await dispatch(createExpenseAccounts(values.expenseAccounts))
            }

            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                t(`notification:settings.expense_account.${msgKey}.title`),
                t(`notification:settings.expense_account.${msgKey}.message`, { count })
            )

            onHide()
        } catch (err) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_account.update.title"))
        }
    }

    const onHide = () => {
        setIsLoading(false)
        onCancel()
    }

    return (
        <Modal
            title={t("action:expense_account_settings.expense_accounts")}
            visible={isShowing}
            maskClosable={false}
            onOk={formInstance.submit}
            onCancel={onHide}
            okText={t(`action:expense_account_settings.${isEdit ? "edit" : "save"}`)}
            cancelText={t("action:cancel")}
            confirmLoading={isLoading}
            keyboard
            destroyOnClose
        >
            <ExpenseAccountForm formInstance={formInstance} onSubmit={onSubmit} isEdit={isEdit} expenseAccounts={expenseAccounts} />
        </Modal>
    )
}

export default ExpenseAccountModal
